import React, { useCallback, useEffect, useRef, useState } from 'react';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001';

// ─────────────────────────────────────────────────────────────────────────────
// "WE COULDN'T CONFIRM YOUR LICENSE" — AND, AT LAST, SOMEWHERE TO SEND IT.
//
// 🚨 THIS IS THE HALF THAT DID NOT EXIST. The derivation, the copy, the
// suppression rules and the table were all built and wired to DoorlyDesk only,
// so a partner whose register check came back negative was held, told nothing,
// and given nowhere to send anything. The admin panel reported "asks for a
// document: true" about a request that was never made. Toni Cramond and Illy
// Hassan are both in that state today.
//
// ⚠️ IT RENDERS ITSELF OR NOTHING, and nothing is the common case. The server
// answers `request: null` for a positive result, a pending one, a source
// outage, a missing number and a disciplinary status — every case where a
// document cannot help. A partner whose licence checks out is shown no badge,
// no notice and no reassurance.
//
// 🔑 THE COPY IS THE SERVER'S. One sentence, one place — the dashboard notice,
// the 403 on a blocked submission and this card all read the same wording, so
// a partner cannot be told two different things about one hold.
// ─────────────────────────────────────────────────────────────────────────────

export default function LicenseEvidenceCard({ token, onChange }) {
  const [request, setRequest] = useState(null);
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState('');
  const [done, setDone] = useState('');
  const fileRef = useRef(null);

  const load = useCallback(() => {
    if (!token) return;
    fetch(`${API_URL}/api/doorlypro/me/license-evidence`,
      { headers: { Authorization: `Bearer ${token}` } })
      .then((r) => r.json())
      .then((d) => setRequest(d.request || null))
      .catch(() => setRequest(null));
  }, [token]);

  useEffect(load, [load]);

  const submit = async (file) => {
    if (!file) return;
    setBusy(true); setError(''); setDone('');
    try {
      const body = new FormData();
      body.append('file', file);
      const res = await fetch(`${API_URL}/api/doorlypro/me/license-evidence`, {
        method: 'POST', headers: { Authorization: `Bearer ${token}` }, body,
      });
      const d = await res.json();
      // 🔑 THE SERVER'S SENTENCE. "Your license is already with our team" and
      // "upload it as a PDF or a photo" are different problems with different
      // answers, and a generic failure throws both away.
      if (!res.ok) { setError(d.error || 'That did not upload. Please try again.'); return; }
      setDone(d.message || 'Thanks — your license is with our team.');
      load();
      if (onChange) onChange();
    } catch (_) {
      setError('Could not reach the server. Nothing was uploaded.');
    } finally {
      setBusy(false);
      // ⚠️ CLEARED EITHER WAY, so choosing the same file again fires onChange.
      // Without this a partner who picks the wrong file, sees the refusal, and
      // re-picks the right one gets nothing on the second attempt.
      if (fileRef.current) fileRef.current.value = '';
    }
  };

  if (!request) return null;

  return (
    <div style={{ marginBottom: 20, padding: '14px 16px', background: '#fffbeb',
      border: '1px solid #fde68a', borderRadius: 10 }}>
      <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 4 }}>
        We couldn&rsquo;t confirm your license
      </div>
      <p style={{ margin: '0 0 10px', fontSize: 13, color: '#4b5563', lineHeight: 1.6 }}>
        {request.copy}
      </p>

      {/* 🚨 ALREADY SENT IS ITS OWN STATE, NOT A DISABLED BUTTON. Being asked
          twice for a document already sent is what a partner wrote in about. */}
      {request.awaiting_review ? (
        <div style={{ fontSize: 13, color: '#166534', background: '#f0fdf4',
          border: '1px solid #bbf7d0', borderRadius: 8, padding: '9px 12px' }}>
          Your license is with our team. We&rsquo;ll come back to you, usually within one
          business day — there&rsquo;s nothing else to send.
        </div>
      ) : (
        <>
          <input ref={fileRef} type="file" accept={(request.accepts || []).join(',')}
            disabled={busy}
            onChange={(e) => submit(e.target.files && e.target.files[0])}
            style={{ fontSize: 13 }} />
          <div style={{ fontSize: 11.5, color: '#9ca3af', marginTop: 6, lineHeight: 1.5 }}>
            {/* ⚠️ THE LIMITS ARE STATED BEFORE THE REFUSAL, not after it. */}
            A PDF or a photo (PNG, JPEG or HEIC), up to{' '}
            {Math.round((request.max_bytes || 0) / (1024 * 1024))} MB. A clear photo of the
            license is fine.
          </div>
        </>
      )}

      {busy && (
        <div style={{ fontSize: 13, color: '#6b7280', marginTop: 8 }}>Uploading…</div>
      )}
      {error && (
        <div style={{ fontSize: 13, color: '#ff082f', marginTop: 8 }}>{error}</div>
      )}
      {done && !request.awaiting_review && (
        <div style={{ fontSize: 13, color: '#166534', marginTop: 8 }}>{done}</div>
      )}
    </div>
  );
}
